import { CheckCircle, Clock, RefreshCw } from "lucide-react";
import { Spinner } from "@/components/ui/spinner";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Draft } from "@/types/types";
import { Button } from "@/components/ui/button";
import { useState } from "react";

// Helper functions
const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const isVideo = (url: string) => {
  return (
    url.match(/\.(mp4|webm|ogg|mov|avi|wmv|flv|mkv|quicktime)($|\?)/i) !== null
  );
};

export default function DraftCard({
  draft,
  openMediaPreview,
  openRevisionDialog,
  handleUpdateDraft,
}: {
  draft: Draft;
  openMediaPreview: (url: string | undefined) => void;
  openRevisionDialog: (draft: Draft) => void;
  handleUpdateDraft: (
    field: string,
    newValue: string,
    draft: Draft,
    setLoading: (loading: boolean) => void
  ) => Promise<void>;
}) {
  const [loading, setLoading] = useState(false);

  return (
    <Card className="overflow-hidden">
      <CardHeader className="pb-0">
        <div className="flex justify-between items-center">
          <Badge
            variant={
              draft.status === "approved"
                ? "success"
                : draft.status === "revision"
                ? "destructive"
                : "secondary"
            }
            className="capitalize"
          >
            {draft.status === "pending" && <Clock className="mr-1 size-3" />}
            {draft.status === "approved" && (
              <CheckCircle className="mr-1 size-3" />
            )}
            {draft.status === "revision" && (
              <RefreshCw className="mr-1 size-3" />
            )}
            {draft.status}
          </Badge>
          <span className="text-xs text-muted-foreground">
            {formatDate(draft.uploadDate)}
          </span>
        </div>
      </CardHeader>

      <CardContent>
        <div
          className="aspect-video bg-muted rounded-md overflow-hidden cursor-pointer"
          onClick={() => openMediaPreview(draft?.signed_url)}
        >
          {isVideo(draft.url) ? (
            <div className="relative w-full h-full">
              <video
                src={draft?.signed_url}
                className="w-full h-full object-cover"
                preload="metadata"
              />
              <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                <Button
                  variant="outline"
                  size="sm"
                  className="pointer-events-none"
                >
                  Play Video
                </Button>
              </div>
            </div>
          ) : (
            <img
              src={draft?.signed_url || "/placeholder.svg"}
              alt="Draft content"
              className="w-full h-full object-cover"
            />
          )}
        </div>

        {draft.notes && draft.status !== "approved" && (
          <div
            className="mt-2 p-2 bg-muted rounded-md text-sm max-h-16 w-full hover:cursor-pointer"
            onClick={() => openRevisionDialog(draft)}
          >
            <p className="font-medium text-xs">Revision notes:</p>
            <p className="text-xs truncate">{draft.notes}</p>
          </div>
        )}
      </CardContent>

      <CardFooter className="pt-0 flex flex-col gap-2">
        <div className="w-full flex gap-2">
          {draft.status !== "approved" && (
            <Button
              variant="default"
              size="sm"
              className="flex-1"
              onClick={() =>
                handleUpdateDraft("status", "approved", draft, setLoading)
              }
              disabled={loading}
            >
              Approve
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            className={draft.status === "approved" ? "w-full" : ""}
            onClick={() => openRevisionDialog(draft)}
            disabled={loading}
          >
            {draft.status === "revision"
              ? "Update Revision"
              : "Request Revision"}
          </Button>
        </div>

        <div className="text-xs w-full flex justify-end text-primary/40 h-4">
          {loading ? (
            <>
              <Spinner className="w-4 h-4 mr-1" /> <p>Updating</p>
            </>
          ) : (
            ""
          )}
        </div>
      </CardFooter>
    </Card>
  );
}
